/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Space,
  useColorMode,
  useColorStyle,
  ModalBody,
  Flex,
  Icon,
  Text,
  Checkbox,
  Divider,
} from '@tonic-one/react';
import { AngleRightIcon, DeleteIcon } from '@tonic-one/react-icons';
import IconButton from '@Components/IconButton';
import {
  FormLabel,
  FormInput,
} from '../../../../../components/Common/FormComponent';
import React, { Key, useCallback, useEffect, useState } from 'react';
import CasTreeTransfer from '../../../../../components/CasTreeTransfer';
import { TreeNode } from '../../../../../components/Tree/type';
import {
  FlexContainer,
  SearchInput,
} from '../../../../../components/CasTreeTransfer/Components';
import Tree from '../../../../../components/Tree';
import useTreeStyle from '../../../../../components/Tree/style';
import { TreeDataType } from '../../../../../components/CasTreeTransfer/type';
import { DashboardApi, GlobalApi } from '@Apis';
import { useTranslation } from 'react-i18next';
import { FlexList } from '@Components/HeaderFieldList';
import { ConditionalAccessSaveProps } from '@Types/globalSetting';
import { DashboardPeriod } from '@Types/dashboard';
import CSpinner from '@Components/CSpinner';
import ErrorAlert from '@Components/Common/ErrorAlert';
import { PermissionNameEnum } from '@Types/permissions';
import { usePermissionStore } from '@Models/usePermissionModel';

const TargetList = React.memo(
  ({
    list,
    width,
    onDel,
    height,
    size,
    title,
    placeholder,
  }: {
    list: any[];
    limit: number;
    width: string;
    onDel: (key: string) => void;
    height: string;
    size: number;
    title: string;
    placeholder?: string;
  }) => {
    const { t } = useTranslation();
    const [readonlyList, setReadonlyList] = useState<any[]>([]);
    const [displayList, setDisplayList] = useState<any[]>([]);
    const [inputValue, setInputValue] = useState<string>('');
    const heightTmp = React.useMemo(() => {
      if (height) {
        return height;
      } else {
        const baseHeight = 86;
        const showSize = size || 4;
        const height = baseHeight + showSize * 32;
        return `${height}px`;
      }
    }, [size, height]);
    useEffect(() => {
      setDisplayList(list);
      setReadonlyList(list);
      setInputValue('');
    }, [list]);
    const search = (keyword: string) => {
      if (readonlyList.length === 0) {
        return;
      }
      if (keyword !== '' && readonlyList.length !== 0) {
        const temp = readonlyList.filter((data) => {
          const lowerCaseKeyword = keyword.toLowerCase();
          const titleMatch =
            data.title.toLowerCase().indexOf(lowerCaseKeyword) > -1;
          return titleMatch;
        });
        setDisplayList(temp);
      } else {
        setDisplayList(readonlyList);
      }
    };
    return (
      <div>
        <FlexList width={width} direction="column" height={heightTmp}>
          <Flex mb="12px">
            <Text>{title}</Text>
          </Flex>
          <Flex mb="12px">
            <SearchInput
              onChange={(e: any) => {
                search(e.target.value);
              }}
              onClearInput={() => {
                search('');
              }}
              value={inputValue}
              placeholder={
                placeholder || t('policy.placeholder.selectedTargets.items')
              }
              autoClear
              width="100%"
            />
          </Flex>
          <Divider mb="12px" />
          <Flex direction="column">
            {displayList.length !== 0 &&
              displayList.map((item) => {
                return (
                  <Flex
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Text ml="8px">{item.title}</Text>
                    <Flex>
                      <IconButton mr="4x">
                        <Icon as={DeleteIcon} onClick={() => onDel(item.key)} />
                      </IconButton>
                    </Flex>
                  </Flex>
                );
              })}
          </Flex>
        </FlexList>
      </div>
    );
  }
);

const EditConditionalAccessPolicyList = React.memo(
  (props: {
    treeData: TreeNode[];
    rightTreeData: TreeNode[];
    setDataSourceRightOnSave: (dataSourceRight: TreeDataType) => void;
  }) => {
    const { t } = useTranslation();
    const [treeStyle] = useState(useTreeStyle());
    const [searchText, setSearchText] = useState<string>('');
    const [checkAll, setCheckAll] = useState<boolean>(false);
    const [checkListLeft, setCheckListLeft] = useState<Key[]>([]);
    const [downCheckListLeft, setDownCheckListLeft] = useState<Key[]>([]);
    const handleCheckListLeftChange = React.useCallback((value: Key[]) => {
      setCheckListLeft(value);
    }, []);
    const handleDownCheckListLeftChange = React.useCallback((value: Key[]) => {
      setDownCheckListLeft(value);
    }, []);
    const [indeterminateLeft, setIndeterminateLeft] = useState<boolean>(false);
    const [readOnlyAllKeyLeft, setReadOnlyAllKeyLeft] = useState<Key[]>(
      props.treeData.map((node) => node.key)
    );
    const handleIndeterminateLeftChange = useCallback((boo: boolean) => {
      setIndeterminateLeft(boo);
    }, []);
    const [dataSourceRight, setDataSourceRight] = useState<TreeDataType>({
      treeData: [],
    });
    const [searchTreeData, setSearchTreeData] = useState<TreeDataType>({
      treeData: [],
    });
    const onDelUsers = (key: string) => {
      const tmp: TreeDataType = { treeData: [] };
      for (const node of dataSourceRight.treeData) {
        if (node.key !== key) {
          tmp.treeData.push(node);
        }
      }
      setDataSourceRight(tmp);
    };

    function keyExists(key: Key): boolean {
      return dataSourceRight.treeData.some((node) => node.key === key);
    }
    function findTitle(key: Key): TreeNode {
      const node = props.treeData.find((node) => node.key === key);
      if (node) {
        return node;
      }
      throw new Error('key not found');
    }

    function findTitleInDown(key: Key): TreeNode {
      const node = searchTreeData.treeData.find((node) => node.key === key);
      if (node) {
        return node;
      }
      throw new Error('key not found');
    }

    const handleTransfer = useCallback(() => {
      const tmp: TreeDataType = { treeData: [] };
      for (const checked of checkListLeft) {
        if (!keyExists(checked)) {
          tmp.treeData.push(findTitle(checked));
        }
      }
      for (const node of dataSourceRight.treeData) {
        if (tmp.treeData.every((item) => item.key !== node.key)) {
          tmp.treeData.push(node);
        }
      }
      for (const checked of downCheckListLeft) {
        if (!keyExists(checked)) {
          tmp.treeData.push(findTitleInDown(checked));
        }
      }
      setDataSourceRight(tmp);
      setCheckListLeft([]);
      setDownCheckListLeft([]);
      setCheckAll(false);
      handleIndeterminateLeftChange(false);
    }, [checkListLeft, dataSourceRight.treeData, downCheckListLeft]);
    const handleCheckBoxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      handleCheckListLeftChange(e.target.checked ? readOnlyAllKeyLeft : []);
      setCheckAll(e.target.checked);
      handleIndeterminateLeftChange(false);
    };
    const handleSelectNode = useCallback(
      (keyArr: any) => {
        handleCheckListLeftChange([...keyArr].sort());
        setCheckAll(readOnlyAllKeyLeft.length === keyArr.length);
        handleIndeterminateLeftChange(
          keyArr.length && readOnlyAllKeyLeft.length !== keyArr.length
        );
      },
      [readOnlyAllKeyLeft]
    );
    const handleDownSelectNode = useCallback((keyArr: any) => {
      handleDownCheckListLeftChange([...keyArr].sort());
    }, []);
    const handleSearchChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value.trim());
      },
      []
    );
    const handleSearchTree = useCallback(
      async (value: string) => {
        const tmp: TreeDataType = { treeData: [] };
        try {
          const userInfo = (await GlobalApi.getConditionalAccessUsers(value))
            .data;
          for (const user of userInfo) {
            tmp.treeData.push({
              key: user.userId,
              title: user.userDisplayName,
            });
          }
        } catch (error) {
          console.log(`error = ${error}`);
        }
        setSearchTreeData(tmp);
      },
      [searchTreeData]
    );

    useEffect(() => {
      props.setDataSourceRightOnSave(dataSourceRight);
    }, [dataSourceRight.treeData]);

    useEffect(() => {
      setDataSourceRight({ treeData: props.rightTreeData });
      setReadOnlyAllKeyLeft(props.treeData.map((node) => node.key));
    }, [props.treeData]);

    return (
      <>
        <Box alignItems="center">
          <Flex alignItems="center">
            <CasTreeTransfer defaultValue={[]} isLeftOnly>
              <FlexContainer direction="column" width="400px" height="320px">
                <Flex mb="12px" ml="4px">
                  <Text>
                    {t('globalSetting.conditionalAccess.edit.table.title')}
                  </Text>
                </Flex>
                <Divider mb="12px" />
                <Flex>
                  <Checkbox
                    onChange={handleCheckBoxChange}
                    checked={checkAll}
                    indeterminate={indeterminateLeft}
                    ml="8px"
                  />
                  <Text ml="8px">
                    {t(
                      'globalSetting.conditionalAccess.edit.table.subtitle.up'
                    )}
                  </Text>
                </Flex>
                <Flex ml="36px" mt="8px">
                  <Tree
                    treeData={props.treeData}
                    onCheck={handleSelectNode}
                    checkedKeys={checkListLeft as (string | number)[]}
                    style={treeStyle}
                  ></Tree>
                </Flex>
                <Flex direction="column">
                  <SearchInput
                    onChange={handleSearchChange}
                    onSearch={handleSearchTree}
                    value={searchText}
                    placeholder={t(
                      'globalSetting.conditionalAccess.edit.table.down.placeholder'
                    )}
                    autoClear
                    width="100%"
                  />
                  <Flex direction="row" mt="8px">
                    <Text ml="8px">
                      {t(
                        'globalSetting.conditionalAccess.edit.table.subtitle.down'
                      )}
                    </Text>
                  </Flex>
                </Flex>
                <Flex ml="36px" mt="8px">
                  <Tree
                    treeData={searchTreeData.treeData}
                    onCheck={handleDownSelectNode}
                    checkedKeys={downCheckListLeft as (string | number)[]}
                    style={treeStyle}
                  ></Tree>
                </Flex>
              </FlexContainer>
            </CasTreeTransfer>
            <Flex
              direction="column"
              alignItems="center"
              justifyContent="center"
              // ml="12px"
              mr="26px"
              width="24px"
            >
              <Button
                variant="secondary"
                onClick={handleTransfer}
                mb="8px"
                width="24px"
                size="sm"
              >
                <Icon
                  as={AngleRightIcon}
                  color={
                    checkListLeft.length > 0 || downCheckListLeft.length > 0
                      ? 'blue:50'
                      : 'white:primary'
                  }
                />
              </Button>
            </Flex>
            <TargetList
              list={dataSourceRight.treeData}
              onDel={onDelUsers}
              limit={500}
              width="400px"
              height="320px"
              size={10}
              title={t('policy.label.selectedTargets')}
            />
          </Flex>
        </Box>
      </>
    );
  }
);

interface TopRiskUserList {
  key: string;
  title: string;
  userName: string;
}

interface RightTreeData {
  key: string;
  title: string;
  userName: string;
}

const EditConditionalAccessPolicyModal = (props: {
  isVisible: boolean;
  onClose: () => void;
  data: {
    policyName: string;
    action: string;
    users: {
      userId: string;
      userName: string;
      userPrincipalName: string;
    }[];
    id: string;
    [key: string]: any;
  };
  setRefreshTrigger: (date: Date) => void;
}) => {
  const { isVisible, onClose, data } = props;
  const { t } = useTranslation();
  const { widthEditablePermission } = usePermissionStore();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [topRiskUserList, setTopRiskUserList] = useState<TopRiskUserList[]>([]);
  const [rightTreeData, setRightTreeData] = useState<RightTreeData[]>([]);
  const [dataSourceRightOnSave, setDataSourceRightOnSave] =
    useState<TreeDataType>();
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);

  const loadData = async () => {
    const topRiskUser: { '1M': any[]; '1w': any[]; '24h': any[] } = (
      await DashboardApi.getDashboardWidget(
        'riskEvent',
        ['visionone', 'microsoft'],
        DashboardPeriod.last24h
      )
    ).data.topRiskyUser;
    const topRiskUserListTmp = topRiskUser['24h'].map((user) => {
      return {
        key: user.key,
        title: user.userName,
        userName: user.userName,
      };
    });
    const rightTreeDataTmp = data.users.map((user) => {
      return {
        key: user.userPrincipalName,
        title: user.userName,
        userName: user.userName,
      };
    });
    setRightTreeData(rightTreeDataTmp);
    setTopRiskUserList(topRiskUserListTmp);
  };

  const onSave = useCallback(async () => {
    try {
      setLoading(true);
      const includeUsers = dataSourceRightOnSave?.treeData.map((user) => {
        return user.key;
      });
      const conditionalAccessPolicy: ConditionalAccessSaveProps = {
        ...data,
        includeUsers,
      };
      delete conditionalAccessPolicy.users;
      delete conditionalAccessPolicy.id;
      await GlobalApi.saveConditionalAccessPolicy(
        data.id,
        conditionalAccessPolicy
      );
    } catch (error) {
      setErrorMsg(`Error: ${error}`);
    } finally {
      setLoading(false);
      props.onClose();
      props.setRefreshTrigger(new Date());
    }
  }, [dataSourceRightOnSave]);

  useEffect(() => {
    (async function doCall() {
      if (!isVisible) {
        return;
      }
      await loadData();
    })();
  }, [isVisible]);

  return (
    <>
      {loading && <CSpinner />}
      {errorMsg && (
        <ErrorAlert
          errorMsg={errorMsg}
          onClose={() => setErrorMsg(undefined)}
        />
      )}
      <Modal
        ensureFocus={true}
        autoFocus={false}
        closeOnEsc={true}
        closeOnOutsideClick={false}
        isClosable={true}
        isOpen={isVisible}
        onClose={onClose}
        size="xl"
      >
        <ModalOverlay opacity={0.9} />
        <ModalContent backgroundColor={colorStyle.background.secondary}>
          <ModalHeader>
            {t('globalSetting.conditionalAccess.edit.title')}
          </ModalHeader>
          <ModalBody ml="24px" mt="16px" mb="16px" overflowY="scroll">
            <Flex direction="column">
              <Flex mb="16px">
                <FormLabel required label={t('policy.label.policyName')} />
                <FormInput disabled placeholder={data.policyName}></FormInput>
              </Flex>
              <Flex mb="16px">
                <FormLabel label={t('dashboard.chart.column.action')} />
                <FormInput disabled placeholder={data.action}></FormInput>
              </Flex>
            </Flex>
            <EditConditionalAccessPolicyList
              treeData={topRiskUserList as TreeNode[]}
              rightTreeData={rightTreeData as TreeNode[]}
              setDataSourceRightOnSave={setDataSourceRightOnSave}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              variant="primary"
              minWidth="20x"
              onClick={onSave}
              disabled={
                !widthEditablePermission(PermissionNameEnum.administrator)
              }
            >
              {t('common.button.save')}
            </Button>
            <Space width="2x" />
            <Button minWidth="20x" onClick={onClose}>
              {t('common.button.cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default React.memo(EditConditionalAccessPolicyModal);
